/**
 * Where a door or window can go, worked out before the engine is asked.
 *
 * The test pass found the controls inviting what the validator then refused: "+ Door"
 * dropped every new door on the same spot, so a second was an error the moment it
 * appeared; the sill and height sliders ran to 72" and 84" on a wall with 69" under its
 * plate; and a shed that got smaller left its openings hanging off the end of a wall.
 * The engine was right every time — but a control that offers a value only to have it
 * refused is a control telling the shopper something untrue.
 *
 * So this file answers three questions about the spec in the controls: where a new
 * opening fits (`placeOpening`), how far each of an opening's sliders may go and what
 * stops it (`openingRanges`), and where the openings land when the shed changes under
 * them (`refitOpenings`, `moveToWall`). Like `LIMITS`, it is *advice* mirrored from the
 * engine — the corner clearance, the gap between openings, the header table, the ridge
 * post — and the engine remains the authority. `test/openings*.test.ts` holds every
 * answer here to what `buildPlan` actually accepts, at the edge.
 */

import {
  DEFAULT_GROUND_SNOW_LOAD,
  DEFAULT_RIDGE_TWELFTHS,
  inches,
  type DoorHand,
  type DoorSwing,
  type Opening,
  type Sixteenths,
  type Spec,
  type WallName,
} from "@planforge/wasm";

import { LIMITS, offsetRange, openingEdgeClearance, toInches, wallThickness } from "./spec.js";

/** A stretch of one wall, from `low` to `high`, measured from its low-coordinate end. */
interface Span {
  low: number;
  high: number;
}

/**
 * The depth of each header the table has, in the order the engine asks for them: the
 * two-by rows by the board's dressed width, then the two LVL plies (spec version 17),
 * both deeper than any two-by.
 */
const HEADER_DEPTHS: readonly Sixteenths[] = [
  inches(3.5),
  inches(5.5),
  inches(7.25),
  inches(9.25),
  inches(11.25),
  inches(11.875),
  inches(14),
];

/**
 * The widest rough opening each two-ply header carries, in inches, by ground snow load —
 * 2x4, 2x6, 2x8, 2x10, 2x12, then 1 3/4" LVL 11 7/8" and 14" deep. Mirrors the `HeaderSnow*`
 * rows of the span table in `engine/src/structural.cpp`: the old table to 2x10 at 30 psf,
 * a 2x12 below it, each heavier column scaled from those by the root of the loads, and
 * the LVL rows the engine sizes by statics and asks for only past every two-by. The
 * configurator writes every design at today's version, so these are the only rows it
 * needs.
 */
const HEADER_REACH: Readonly<Record<number, readonly number[]>> = {
  30: [30, 48, 60, 74, 85, 161, 190],
  40: [26, 42, 53, 66, 76, 149, 174],
  50: [24, 39, 48, 60, 69, 136, 159],
  70: [21, 33, 42, 52, 60, 118, 137],
};

/** The ground snow load this design's roof is sized for: the spec's, or the default. */
export function groundSnowLoadOf(spec: Spec): number {
  return spec.groundSnowLoad ?? DEFAULT_GROUND_SNOW_LOAD;
}

/** The headers the engine will put over an opening in this design, lightest first. */
function headers(spec: Spec): readonly { depth: Sixteenths; reaches: Sixteenths }[] {
  const reach = HEADER_REACH[groundSnowLoadOf(spec)] ?? HEADER_REACH[DEFAULT_GROUND_SNOW_LOAD]!;
  return HEADER_DEPTHS.map((depth, i) => ({ depth, reaches: inches(reach[i]!) }));
}

/** The deepest header the table has: what an opening past every row is measured with. */
const DEEPEST_HEADER = HEADER_DEPTHS[HEADER_DEPTHS.length - 1]!;

/** How deep a header the engine puts over an opening this wide; undefined past the table. */
export function headerDepth(spec: Spec, width: number): Sixteenths | undefined {
  return headers(spec).find((header) => width <= header.reaches)?.depth;
}

/** Sixteenths in a foot, which the bearing arithmetic is worked in. */
const FOOT = inches(12);

/** Where an opening stands in its wall, which is all its jacks' count depends on. */
interface Placed {
  wall: WallName;
  offset: number;
  width: number;
}

/** A jack stud's thickness, and so how far each one moves a king: 1 1/2" in either wall. */
const STUD = inches(1.5);

/** The roof's design load, snow plus ten of dead, in psf. Mirrors `roofLoadPsf`. */
function roofPsf(spec: Spec): number {
  return groundSnowLoadOf(spec) + 10;
}

/**
 * What one jack stud bears before it crushes the plate under it, in pounds: SPF's 425 psi
 * across the plate's grain over the jack's end. Mirrors `jackBearingPounds`.
 */
function jackBearing(spec: Spec): number {
  return Math.trunc((425 * STUD * wallThickness(spec)) / (inches(1) * inches(1)));
}

/**
 * The most jack studs under one end of a header: a pair, from spec version 13, where one
 * would crush the plate. Mirrors `kMaxJackPlies`; the configurator writes every design at
 * today's version, so a pair is always on offer.
 */
const MAX_JACK_PLIES = 2;

/**
 * The widest rough opening whose header's jack studs bear what the roof hands them, on
 * this wall, with `plies` jacks under each end — or Infinity on a gable end, which carries
 * no roof onto its openings. Mirrors `widestOpeningJacksBear` (`engine/src/framing.cpp`) to
 * the sixteenth: the roof's weight (snow plus ten) over half the width and the eave
 * overhang, half of it to each end of a header that runs between the centers of the
 * jacks under its ends, held to what SPF's 425 psi across the plate's grain allows under
 * that many jacks' ends. With the default, a pair, it is the widest the engine builds at
 * all; with one, it is where the engine starts doubling them.
 */
export function widestJacksBear(spec: Spec, wall: WallName, plies = MAX_JACK_PLIES): number {
  if (wall !== "left" && wall !== "right") {
    return Infinity;
  }
  const psf = roofPsf(spec);
  const tributary = Math.trunc(spec.footprint.width / 2) + spec.roof.eaveOverhang;
  const jacks = STUD * plies;
  const bearing = jackBearing(spec) * plies;
  const reaction = (span: number): number => Math.ceil((psf * tributary * span) / (FOOT * FOOT * 2));
  let span = Math.trunc((bearing * FOOT * FOOT * 2) / (psf * tributary));
  while (span > 0 && reaction(span) > bearing) {
    span -= 1;
  }
  return span - jacks;
}

/**
 * What one end of the ridge beam hands the post under it, in pounds: the roof over half
 * the width, per foot and rounded down, times half the length. Mirrors `ridgeReaction` as
 * `resolveFraming` asks it. Nothing on a roof with no post.
 */
function ridgeReaction(spec: Spec): number {
  if (spec.roof.support !== "ridgeBeam") {
    return 0;
  }
  const perFoot = Math.trunc((roofPsf(spec) * Math.trunc(spec.footprint.width / 2)) / FOOT);
  return Math.trunc((perFoot * spec.footprint.length) / (FOOT * 2));
}

/** Whether a ridge post stands on this opening's header. Mirrors `carriesRidgePost`. */
function carriesPost(spec: Spec, opening: Placed): boolean {
  const post = ridgePost(spec, opening.wall);
  return post !== undefined && opening.offset < post.high && opening.offset + opening.width > post.low;
}

/**
 * How many jack studs stand under each end of an opening's header: one, or two where one
 * would crush the plate under it — from the roof an eave wall's header carries, or the
 * ridge post's share, by lever arm, on a gable wall's header under the post. Mirrors
 * `headerBearingOf` (`engine/src/framing.cpp`), and `test/openings*.test.ts` holds it to the
 * jacks `buildPlan` frames. Both ends get the same count.
 */
export function jackPlies(spec: Spec, opening: Placed): number {
  if (carriesPost(spec, opening)) {
    const reaction = ridgeReaction(spec);
    const at = ridgeX(spec);
    const nearer = (plies: number): number => {
      const reach = (STUD * plies) / 2;
      const left = opening.offset - reach;
      const right = opening.offset + opening.width + reach;
      const span = right - left;
      const arm = (length: number): number => Math.min(Math.max(length, 0), span);
      const share = (lever: number): number => Math.ceil((reaction * lever) / span);
      return Math.max(share(arm(right - at)), share(arm(at - left)));
    };
    return nearer(1) > jackBearing(spec) ? MAX_JACK_PLIES : 1;
  }
  return opening.width > widestJacksBear(spec, opening.wall, 1) ? MAX_JACK_PLIES : 1;
}

/** How much further an opening's framing reaches past its edge than one jack's: a stud a doubled jack. */
function extraReach(spec: Spec, opening: Placed): number {
  return (jackPlies(spec, opening) - 1) * STUD;
}

/**
 * The wall an opening has to leave at each corner: the corner framing, its king and its
 * jacks — a stud more where they are doubled. Mirrors the engine's per-opening
 * `openingEdgeClearance`.
 */
export function edgeClearance(spec: Spec, opening: Placed): number {
  return openingEdgeClearance(spec) + extraReach(spec, opening);
}

/**
 * The wall two neighboring openings have to leave between them: each one's king and
 * jacks, a stud more for each that stands on a pair. Mirrors `checkOpeningSpacing`.
 */
export function gapBetween(
  spec: Spec,
  a: Placed,
  b: Placed,
): number {
  return LIMITS.openingGap + extraReach(spec, a) + extraReach(spec, b);
}

/** Why a slider stops at the jacks, in the shopper's words. */
function jacksWhy(spec: Spec): string {
  return `Under ${String(groundSnowLoadOf(spec))} psf of snow, a wider opening in this wall would crush the plate under its jack studs, even doubled. Frame the walls in 2x6, or narrow the shed, to go wider.`;
}

/** One plate is a 2x laid flat, whichever stock the walls are. */
const PLATE = inches(1.5);

/**
 * How far a roof at this pitch climbs over a level run, rounded the way the engine rounds
 * it: half away from zero, to the sixteenth. Mirrors `Framing::riseFor`, whose pitch is
 * scaled by a thousand before it divides — which comes to the same quotient.
 */
function riseFor(spec: Spec, run: number): number {
  const product = run * spec.roof.pitch.rise;
  const over = spec.roof.pitch.run;
  return over === 0 ? 0 : Math.sign(product) * Math.floor((Math.abs(product) + over / 2) / over);
}

/**
 * Where a saltbox's ridge stands, in twelfths of the width: the spec's, or the two thirds
 * every saltbox had before the choice. Mirrors `ridgeTwelfthsOf`.
 */
export function ridgeTwelfthsOf(spec: Spec): number {
  return spec.roof.ridgeTwelfths ?? DEFAULT_RIDGE_TWELFTHS;
}

/**
 * Where the two slopes meet, across from the left wall's outer face: the middle of a
 * gable, and `ridgeTwelfths` of the way across a saltbox, whose short slope runs the
 * width's remaining twelfths to the sixteenth, rounded down. Mirrors `Framing::ridgeX`.
 */
export function ridgeX(spec: Spec): number {
  const width = spec.footprint.width;
  return spec.roof.style === "saltbox"
    ? width - Math.trunc((width * (12 - ridgeTwelfthsOf(spec))) / 12)
    : Math.trunc(width / 2);
}

/**
 * The top of a wall's plates, from the subfloor. Every wall's is the spec's wall height
 * but the right one under a lean-to or a saltbox. A lean-to's stands taller by the
 * roof's climb from the low wall's outside face to its own inside face, so the rafter's
 * second birdsmouth seats on it; a saltbox's by what the long slope climbs to the ridge
 * less what the short one does, so the two arrive there together. Mirrors
 * `Framing::wallTopOf`.
 */
export function wallTop(spec: Spec, wall: WallName): number {
  if (wall !== "right") {
    return spec.wallHeight;
  }
  const width = spec.footprint.width;
  switch (spec.roof.style) {
    case "leanTo":
      return spec.wallHeight + riseFor(spec, width - wallThickness(spec));
    case "saltbox":
      return spec.wallHeight + riseFor(spec, ridgeX(spec)) - riseFor(spec, width - ridgeX(spec));
    case "gable":
      return spec.wallHeight;
  }
}

/**
 * The height the studs stop at in a wall, from the subfloor: the top of its plates less
 * the two top plates. A header has to fit under it. Mirrors `Framing::studTopOf`.
 */
export function studTop(spec: Spec, wall: WallName): number {
  return wallTop(spec, wall) - PLATE * 2;
}

/** How long a wall runs between its corners, as an opening's offset measures it. */
export function wallRun(spec: Spec, wall: WallName): number {
  return wall === "front" || wall === "back"
    ? spec.footprint.width
    : spec.footprint.length - wallThickness(spec) * 2;
}

/**
 * Where an open vault's ridge post stands in a gable wall: two plies of the wall's own
 * stock, centered under the ridge — off the middle of a saltbox's. Mirrors
 * `ridgePostZone`. A tied roof has no post.
 */
function ridgePost(spec: Spec, wall: WallName): Span | undefined {
  if (spec.roof.support !== "ridgeBeam" || (wall !== "front" && wall !== "back")) {
    return undefined;
  }
  const at = ridgeX(spec);
  return { low: at - PLATE, high: at + PLATE };
}

/**
 * How far an opening's framing reaches past each edge: its jacks and the king outside
 * them. Mirrors `framingReach`.
 */
function framingReach(spec: Spec, opening: Placed): number {
  return STUD * (jackPlies(spec, opening) + 1);
}

/**
 * Whether the ridge post would stand in this opening's jacks or kings. A post across the
 * opening stands on its header, which runs out over the jacks, so only a ply past them is
 * in a king; a post clear of the opening runs down to the plate, through whatever jack or
 * king it lands on. Mirrors `ridgePostInFraming`, refused as `ridge_post_in_opening_framing`.
 */
export function postInFraming(spec: Spec, opening: Placed): boolean {
  const post = ridgePost(spec, opening.wall);
  if (post === undefined) {
    return false;
  }
  const left = opening.offset;
  const right = opening.offset + opening.width;
  const reach = framingReach(spec, opening);
  if (post.high <= left - reach || post.low >= right + reach) {
    return false;
  }
  if (!carriesPost(spec, opening)) {
    return true;
  }
  const jacks = STUD * jackPlies(spec, opening);
  return post.low < left - jacks || post.high > right + jacks;
}

/**
 * The stretches of a wall where a new opening `width` wide could stand, leaving the
 * corners their clearance and every opening already there its gap — each a stud wider
 * where either one's jacks are doubled. `except` is an opening to leave out — the one
 * being moved, so it does not stand in its own way.
 *
 * The ridge post is kept clear too, though the engine will build a door under it: that
 * door's header carries the whole ridge, and a new opening should not start life as a
 * beam somebody has to size. A shopper can still slide one there. So a newcomer carries
 * no post, whether its jacks are doubled is its width's question alone, and its jacks and
 * king keep out of the post as well as its rough opening (`postInFraming`).
 */
function freeStretches(spec: Spec, wall: WallName, width: number, except?: string): Span[] {
  const newcomer = { wall, offset: 0, width };
  const clearance = edgeClearance(spec, newcomer);
  const blocked: Span[] = spec.openings
    .filter((opening) => opening.wall === wall && opening.id !== except)
    .map((opening) => {
      const gap = gapBetween(spec, opening, newcomer);
      return { low: opening.offset - gap, high: opening.offset + opening.width + gap };
    });
  const post = ridgePost(spec, wall);
  if (post !== undefined) {
    const reach = framingReach(spec, newcomer);
    blocked.push({ low: post.low - reach, high: post.high + reach });
  }
  blocked.sort((a, b) => a.low - b.low);

  const free: Span[] = [];
  let from: number = clearance;
  const to = wallRun(spec, wall) - clearance;
  for (const block of blocked) {
    if (block.low > from) {
      free.push({ low: from, high: Math.min(block.low, to) });
    }
    from = Math.max(from, block.high);
  }
  if (to > from) {
    free.push({ low: from, high: to });
  }
  return free.filter((span) => span.high > span.low);
}

/** Where an opening of this width may stand in a stretch, on whole inches: the offset slider's step. */
function inchesWithin(span: Span, width: number): Span | undefined {
  const step = inches(1);
  const low = Math.ceil(span.low / step) * step;
  const high = Math.floor((span.high - width) / step) * step;
  return low > high ? undefined : { low, high };
}

/** An opening of this width centered in a stretch it fits, on a whole inch where one fits. */
function centeredIn(span: Span, width: number): number {
  const within = inchesWithin(span, width);
  if (within === undefined) {
    return span.low;
  }
  const centered = Math.round((span.low + (span.high - span.low - width) / 2) / inches(1)) * inches(1);
  return Math.min(Math.max(centered, within.low), within.high);
}

/** The sizes a new opening starts at — the default shed's door and window. */
const STARTING_SIZE: Record<Opening["type"], { width: number; height: number; sill: number }> = {
  door: { width: inches(38), height: inches(80), sill: 0 },
  window: { width: inches(36), height: inches(30), sill: inches(44) },
};

/** Narrower sizes to fall back on when no wall has room for the starting one. */
const FALLBACK_WIDTHS: Record<Opening["type"], readonly number[]> = {
  door: [inches(38), inches(36), inches(32), inches(30)],
  window: [inches(36), inches(30), inches(24), inches(18)],
};

/**
 * Which walls a new opening tries, in order. A door belongs on the front first; a window
 * goes wherever there is the most wall, and this order only breaks a tie.
 */
const WALL_ORDER: Record<Opening["type"], readonly WallName[]> = {
  door: ["front", "back", "left", "right"],
  window: ["right", "left", "back", "front"],
};

/**
 * The step the width, height and sill sliders move by, from an even start (12", or 0 for a
 * door's sill). Every stop and every refitted sill lands on this grid: a slider cannot
 * reach a value between its steps, so a stop at an odd inch is one the thumb never gets
 * to — and the hint that waits for the thumb at the stop is never shown.
 */
export const SIZE_STEP = inches(2);

const downTo = (value: number, step: number): number => Math.floor(value / step) * step;

/** Openings are keyed by a stable id, so a new one needs a name nothing else has. */
export function nextOpeningId(spec: Spec, type: Opening["type"]): string {
  const used = new Set(spec.openings.map((opening) => opening.id));
  for (let n = 1; ; n += 1) {
    const id = `${type}-${n}`;
    if (!used.has(id)) {
      return id;
    }
  }
}

/**
 * A new door or window, standing in free wall — or undefined when no wall has room for
 * one, which is the "+ Door" button's cue to say so instead of adding an error.
 *
 * It starts at the default shed's size and is made to fit the wall it lands on: a door
 * shorter than 80" when the header would not clear the plate, a window's sill lowered
 * before its glass is made smaller. Only if no wall has room at the starting width does
 * it try narrower ones, so the first door on a shed is the door a shopper expects.
 */
export function placeOpening(spec: Spec, type: Opening["type"]): Opening | undefined {
  const start = STARTING_SIZE[type];
  for (const width of FALLBACK_WIDTHS[type]) {
    const header = headerDepth(spec, width);
    if (header === undefined) {
      continue;
    }
    // Each wall's own room under its plate: on a lean-to the high wall has more of it,
    // and a door that has to come down to fit a low wall need not on the high one.
    const sized = (wall: WallName): { sill: number; height: number } => {
      const room = studTop(spec, wall) - header;
      // A window keeps its glass and comes down the wall first; a door has no sill to
      // lower, so it gets shorter.
      const sill =
        type === "door"
          ? 0
          : Math.max(inches(12), Math.min(start.sill, downTo(room - start.height, SIZE_STEP)));
      return { sill, height: Math.min(start.height, downTo(room - sill, SIZE_STEP)) };
    };

    let best: { wall: WallName; span: Span; rank: number } | undefined;
    WALL_ORDER[type].forEach((wall, rank) => {
      if (sized(wall).height < LIMITS.minOpeningSize || width > widestJacksBear(spec, wall)) {
        return;
      }
      for (const span of freeStretches(spec, wall, width)) {
        if (span.high - span.low < width) {
          continue;
        }
        const better =
          best === undefined ||
          (type === "door"
            ? rank < best.rank ||
              (rank === best.rank && span.high - span.low > best.span.high - best.span.low)
            : span.high - span.low > best.span.high - best.span.low);
        if (better) {
          best = { wall, span, rank };
        }
      }
    });
    if (best !== undefined) {
      const { sill, height } = sized(best.wall);
      return {
        id: nextOpeningId(spec, type),
        wall: best.wall,
        type,
        width: width as Opening["width"],
        height: height as Opening["height"],
        sill: sill as Opening["sill"],
        offset: centeredIn(best.span, width) as Opening["offset"],
      };
    }
  }
  return undefined;
}

/** One slider's reach, and — when it stops short of its own scale — the reason why. */
export interface Range {
  min: number;
  max: number;
  /** Why `max` is where it is, in the shopper's words. Undefined at the slider's own cap. */
  why?: string;
}

export interface OpeningRanges {
  width: Range;
  height: Range;
  sill: Range;
  offset: Range;
}

/**
 * The scale each slider is drawn at before the wall has any say. A door goes to the widest
 * opening the engine frames, which an LVL header spans (spec version 17).
 */
export const SLIDER_CAP: Record<Opening["type"], { width: number; height: number; sill: number }> = {
  door: { width: LIMITS.maxOpeningWidth, height: inches(84), sill: 0 },
  window: { width: inches(60), height: inches(60), sill: inches(72) },
};

/** The lowest a window's sill slider goes. */
export const MIN_SILL = inches(12);

/**
 * A range that still holds the value the opening has now.
 *
 * An opening can already be past where its slider would stop — slid into a neighbor, or
 * left too tall by a wall made lower. Then the slider reaches to where it is, so its thumb
 * shows the truth and dragging it back works, and it gives no reason: the "Cannot build"
 * beside it already says what is wrong, and "stops at 12\"" under a 38" door is nonsense.
 */
function holding(range: Range, value: number): Range {
  if (value > range.max) {
    return { min: range.min, max: value };
  }
  return { ...range, max: Math.max(range.min, range.max) };
}

/** The width a header of the table still fits over, with this much room above the hole. */
function widestUnder(spec: Spec, room: number): number {
  const fits = headers(spec).filter((header) => header.depth <= room);
  return fits.length === 0 ? 0 : fits[fits.length - 1]!.reaches;
}

/**
 * How far each of an opening's sliders may go, given its wall and everything on it.
 *
 * Each range is the range the engine accepts with every *other* value held where it is,
 * so the slider stops at the last value that builds, and `why` says what it ran into —
 * the top plate, the corner, the window next to it — because a slider that stops short
 * for no visible reason reads as a broken slider.
 *
 * The offset runs the whole wall, as it always has, past the other openings: a slider
 * that could not carry a door past a window would trap it on one side for good. Sliding
 * through a neighbor is a moment of "Cannot build" that names them both.
 */
export function openingRanges(spec: Spec, id: string): OpeningRanges | undefined {
  const opening = spec.openings.find((candidate) => candidate.id === id);
  if (opening === undefined) {
    return undefined;
  }
  const cap = SLIDER_CAP[opening.type];
  const isDoor = opening.type === "door";
  const top = studTop(spec, opening.wall);
  const header = headerDepth(spec, opening.width) ?? DEEPEST_HEADER;
  const run = wallRun(spec, opening.wall);
  const raise = isDoor
    ? "Raise the walls to go taller."
    : "Lower the sill or raise the walls to go taller.";

  // Height: the header over it has to fit under the plate.
  const tallest = top - header - opening.sill;
  const height: Range =
    tallest < cap.height
      ? {
          min: LIMITS.minOpeningSize,
          max: downTo(tallest, SIZE_STEP),
          why: `The header over it has to fit under the top plate. ${raise}`,
        }
      : { min: LIMITS.minOpeningSize, max: cap.height };

  // Sill: the same plate, from the other side.
  const highest = top - header - opening.height;
  const sill: Range = isDoor
    ? { min: 0, max: 0 }
    : highest < cap.sill
      ? {
          min: MIN_SILL,
          max: downTo(highest, SIZE_STEP),
          why: "Its header has to fit under the top plate. Make the window shorter or raise the walls to set it higher.",
        }
      : { min: MIN_SILL, max: cap.sill };

  // Width grows away from the offset, so it stops at whatever is next along the wall —
  // or at the widest opening a header still fits over. Past the width one jack bears, the
  // engine doubles them, and the pair reaches a stud further on both sides: so each width
  // is asked whether it still fits as the engine would frame it, and the slider stops at
  // the last that does.
  const ahead = spec.openings
    .filter(
      (other) =>
        other.wall === opening.wall && other.id !== opening.id && other.offset >= opening.offset,
    )
    .sort((a, b) => a.offset - b.offset)[0];
  const behind = spec.openings
    .filter(
      (other) =>
        other.wall === opening.wall && other.id !== opening.id && other.offset < opening.offset,
    )
    .sort((a, b) => b.offset - a.offset)[0];
  // Already in the post is the "Cannot build" beside it, and the offset slider's to fix,
  // as a neighbor it is already into is.
  const post = ridgePost(spec, opening.wall);
  const postAhead =
    post !== undefined &&
    !carriesPost(spec, opening) &&
    !postInFraming(spec, opening) &&
    post.low >= opening.offset + opening.width
      ? post
      : undefined;
  const underPlate = widestUnder(spec, top - opening.sill - opening.height);
  const singly = { ...opening, width: LIMITS.minOpeningSize };
  const stopsAt = (width: number): string | undefined => {
    const sized = { ...opening, width };
    if (width > underPlate) {
      return underPlate === 0
        ? "No header fits above it. Make it shorter to make room."
        : "A wider opening needs a deeper header, and there is no room for one above it. Make it shorter to go wider.";
    }
    if (width > widestJacksBear(spec, opening.wall)) {
      return jacksWhy(spec);
    }
    if (opening.offset + width > run - edgeClearance(spec, sized)) {
      return "It has reached the corner framing. Slide it along the wall to go wider.";
    }
    // The ridge post ahead of it is a neighbor too: its jacks and king stop short of it.
    // Under the post already, a wider opening only takes its far edge further past it.
    if (postAhead !== undefined && opening.offset + width + framingReach(spec, sized) > postAhead.low) {
      return "Its framing has reached the ridge post. Slide it along the wall, clear of the post or under it, to go wider.";
    }
    if (ahead !== undefined && ahead.offset - opening.offset - width < gapBetween(spec, sized, ahead)) {
      return `The ${ahead.type} beside it needs ${String(toInches(gapBetween(spec, sized, ahead) as Sixteenths))}" of wall between them. Slide it along the wall to go wider.`;
    }
    // Its own left side moves only when a wider opening doubles its jacks, and only that
    // is this slider's to stop at: a neighbor or a corner it is already into is the
    // "Cannot build" beside it, and the offset slider's to fix.
    if (jackPlies(spec, sized) > jackPlies(spec, singly)) {
      const cornered =
        opening.offset >= edgeClearance(spec, singly) && opening.offset < edgeClearance(spec, sized);
      const crowded =
        behind !== undefined &&
        opening.offset - behind.offset - behind.width >= gapBetween(spec, singly, behind) &&
        opening.offset - behind.offset - behind.width < gapBetween(spec, sized, behind);
      if (cornered || crowded) {
        return "Any wider and the roof needs its jack studs doubled, which takes wall it has no room for on this side. Slide it along the wall to go wider.";
      }
    }
    return undefined;
  };
  // Every width on the slider's grid, from the top down, until one fits: the first that
  // does is the stop, and what stopped the one above it is why.
  let widest = downTo(cap.width, SIZE_STEP);
  let why: string | undefined;
  while (widest > LIMITS.minOpeningSize) {
    const stop = stopsAt(widest);
    if (stop === undefined) {
      break;
    }
    why = stop;
    widest -= SIZE_STEP;
  }
  const width: Range = {
    min: LIMITS.minOpeningSize,
    max: widest,
    ...(why === undefined || widest >= cap.width ? {} : { why }),
  };

  return {
    width: holding(width, opening.width),
    height: holding(height, opening.height),
    sill: holding(sill, opening.sill),
    offset: offsetFor(spec, opening),
  };
}

/**
 * The offset slider's reach: the wall between the corners' clearance, which is a stud
 * more at each where the opening's jacks are doubled. `offsetRange` with the jacks
 * this opening has.
 */
function offsetFor(spec: Spec, opening: Opening): { min: number; max: number } {
  const range = offsetRange(spec, opening.id);
  const extra = extraReach(spec, opening);
  return { min: range.min + extra, max: Math.max(range.min + extra, range.max - extra) };
}

/**
 * Where an opening the ridge post stands in the framing of (`postInFraming`) can go
 * instead: the nearest offset, between the corners' clearance and clear of every
 * neighbor's gap (`gapBetween`) where the refit puts that neighbor, at which the post is
 * either clear of its king or far enough under it to stand on the header. The opening
 * itself if it is already clear, and undefined if nowhere is: a slide that traded the
 * post for a neighbor would only swap one refusal for another.
 */
function clearOfPost(
  spec: Spec,
  opening: Placed & { id: string },
  neighbors: readonly Placed[],
  low: number,
  high: number,
): number | undefined {
  const post = ridgePost(spec, opening.wall);
  if (post === undefined || !postInFraming(spec, opening)) {
    return opening.offset;
  }
  // Under the post its jacks may double, so each side is asked at both counts and every
  // candidate is held to the rule itself rather than to the arithmetic that found it.
  const candidates: number[] = [];
  for (const plies of [1, MAX_JACK_PLIES]) {
    const jacks = STUD * plies;
    const reach = jacks + STUD;
    candidates.push(
      post.low - reach - opening.width,
      post.high + reach,
      post.high - jacks - opening.width,
      post.low + jacks,
    );
  }
  const clearOfNeighbors = (offset: number): boolean => {
    const moved = { ...opening, offset };
    return neighbors.every((other) => {
      if (other.wall !== opening.wall) {
        return true;
      }
      const gap = gapBetween(spec, moved, other);
      return offset + opening.width + gap <= other.offset || other.offset + other.width + gap <= offset;
    });
  };
  return candidates
    .filter((offset) => offset >= low && offset <= high)
    .filter((offset) => !postInFraming(spec, { ...opening, offset }))
    .filter(clearOfNeighbors)
    .sort((a, b) => Math.abs(a - opening.offset) - Math.abs(b - opening.offset))[0];
}

/**
 * The openings, kept on their walls after the shed changed under them.
 *
 * A wall that got shorter (a narrower shed, a thicker wall stock eating the side walls'
 * run) slides an opening back along it until it clears the corner again; a wall that got
 * lower brings a window's sill down until its header clears the plate. A roof that brings
 * its ridge post into an opening's jacks or kings - a gable made a saltbox, a ridge moved,
 * a wider shed - slides the opening the least it takes to stand clear of the post or
 * under it (`clearOfPost`). Nothing is resized
 * — the size is what the shopper chose, and if it no longer fits the validator says so
 * in words — and nothing that still fits moves at all. Returns the same array when there
 * was nothing to do, so a spec that did not change stays the same object.
 */
export function refitOpenings(spec: Spec): Spec["openings"] {
  // First every opening back inside its corners and under its plate; then, against where
  // that leaves its neighbors, each one the post stands in off the post. In one pass a
  // door could be slid clear of a window's old place and into its refitted one.
  const fitted = spec.openings.map((opening) => {
    // A stud more at each corner where a wider shed, or more snow, has doubled its jacks.
    const clearance = edgeClearance(spec, opening);
    const furthest = Math.max(clearance, wallRun(spec, opening.wall) - opening.width - clearance);
    const offset = Math.min(Math.max(opening.offset, clearance), furthest);

    let sill: number = opening.sill;
    const header = headerDepth(spec, opening.width);
    if (opening.type === "window" && header !== undefined) {
      const highest = studTop(spec, opening.wall) - header - opening.height;
      if (sill > highest) {
        sill = Math.max(MIN_SILL, downTo(highest, SIZE_STEP));
      }
    }
    return { opening, clearance, furthest, offset, sill };
  });

  const placed: Placed[] = fitted.map(({ opening, offset }) => ({ ...opening, offset }));
  fitted.forEach((fit, index) => {
    const neighbors = placed.filter((_, at) => at !== index);
    fit.offset =
      clearOfPost(spec, { ...fit.opening, offset: fit.offset }, neighbors, fit.clearance, fit.furthest) ??
      fit.offset;
    placed[index] = { ...fit.opening, offset: fit.offset };
  });

  let moved = false;
  const refitted = fitted.map(({ opening, offset, sill }) => {
    if (offset === opening.offset && sill === opening.sill) {
      return opening;
    }
    moved = true;
    return { ...opening, offset: offset as Opening["offset"], sill: sill as Opening["sill"] };
  });
  return moved ? refitted : spec.openings;
}

/** Where on a wall an opening could stand that is nearest its offset now, if anywhere. */
function nearestFree(spec: Spec, opening: Opening, wall: WallName): number | undefined {
  if (opening.width > widestJacksBear(spec, wall)) {
    return undefined;
  }
  let nearest: number | undefined;
  for (const span of freeStretches(spec, wall, opening.width, opening.id)) {
    if (span.high - span.low < opening.width) {
      continue;
    }
    const within = inchesWithin(span, opening.width);
    const candidate =
      within === undefined
        ? span.low
        : Math.min(Math.max(opening.offset, within.low), within.high);
    if (
      nearest === undefined ||
      Math.abs(candidate - opening.offset) < Math.abs(nearest - opening.offset)
    ) {
      nearest = candidate;
    }
  }
  return nearest;
}

/**
 * Whether an opening, at the size it is, has somewhere to stand on a wall. Its own wall
 * always counts: the shopper is already there, and the sliders are how they fix it.
 */
export function hasRoomOn(spec: Spec, id: string, wall: WallName): boolean {
  const opening = spec.openings.find((candidate) => candidate.id === id);
  return (
    opening !== undefined &&
    (opening.wall === wall || nearestFree(spec, opening, wall) !== undefined)
  );
}

/**
 * An opening moved to another wall, standing in that wall's free run as near as it can
 * to where it was. The wall buttons offer only walls with room (`hasRoomOn`); asked for one
 * without, it keeps its offset, held inside the corners, and the validator names what is
 * in the way.
 */
export function moveToWall(spec: Spec, id: string, wall: WallName): Opening | undefined {
  const opening = spec.openings.find((candidate) => candidate.id === id);
  if (opening === undefined) {
    return undefined;
  }
  const there = { ...opening, wall };
  const nearest = nearestFree(spec, opening, wall);
  if (nearest !== undefined) {
    return { ...there, offset: nearest as Opening["offset"] };
  }
  const [placed] = refitOpenings({ ...spec, openings: [there] });
  return placed;
}

/**
 * The opening that keeps a saltbox's ridge from moving to `twelfths`, named, or undefined
 * when nothing does.
 *
 * The ridge sets how tall the right wall stands: moving it toward the middle lowers that
 * wall's plate under whatever is framed in it, and a door that fitted can end up with no
 * room for its header. The wall alone decides it, so it is mirrored here (the engine's
 * `opening_too_tall`) and the position is not offered, rather than offered and refused.
 * Moving the ridge never lowers any other wall, and raising this one never blocks.
 */
export function ridgeBlockedBy(spec: Spec, twelfths: number): string | undefined {
  const moved: Spec = { ...spec, roof: { ...spec.roof, ridgeTwelfths: twelfths } };
  const top = studTop(moved, "right");
  for (const [index, opening] of spec.openings.entries()) {
    if (opening.wall !== "right") {
      continue;
    }
    const header = headerDepth(spec, opening.width) ?? DEEPEST_HEADER;
    if (opening.sill + opening.height + header > top) {
      return openingName(spec, index);
    }
  }
  return undefined;
}

/** What one 16d nail holds sideways, and the fewest and most one heel joint gets. Mirrors `kNail16dPounds`, `kMinHeelNails` and `kMaxHeelNails`. */
const NAIL_16D_POUNDS = 135;
const MIN_HEEL_NAILS = 4;
const MAX_HEEL_NAILS = 12;

/**
 * The 16d nails at each end of a rafter tie under this design's roof, or zero with no
 * ties. Mirrors `heelNailsOf` (`engine/src/framing.cpp`) to the pound: the tie's pull is
 * the roof load on one rafter's strip times the width times the pitch's run, over four
 * times its rise, rounded up, and a nail holds 135 pounds of it.
 */
export function heelNails(spec: Spec): number {
  if (spec.roof.style !== "gable" || spec.roof.support !== "tied" || spec.roof.pitch.rise <= 0) {
    return 0;
  }
  const psf = groundSnowLoadOf(spec) + 10;
  const numerator = psf * spec.roof.rafterSpacing * spec.footprint.width * spec.roof.pitch.run;
  const divisor = 4 * spec.roof.pitch.rise * FOOT * FOOT;
  const pull = Math.ceil(numerator / divisor);
  return Math.max(MIN_HEEL_NAILS, Math.ceil(pull / NAIL_16D_POUNDS));
}

/**
 * The first part of the design a ground snow load would refuse, named, with what it would
 * run into — or undefined when everything still builds under it.
 *
 * A heavier load deepens every header and, on a wide shed, narrows what a side wall's
 * jacks carry. The snow load is a fact about the site, so the control offers every tier
 * whatever the doors are: this is how its hint says, before a click, which tier a door
 * stands in the way of. Mirrors the engine's `header_span_exceeded`,
 * `jack_bearing_exceeded` and `opening_too_tall`, and `test/openings*.test.ts` holds it to
 * them.
 */
export function snowBlockedBy(
  spec: Spec,
  groundSnowLoad: number,
): { name: string; why: string } | undefined {
  const loaded: Spec = { ...spec, groundSnowLoad };
  for (const [index, opening] of spec.openings.entries()) {
    const name = openingName(spec, index);
    const header = headerDepth(loaded, opening.width);
    if (header === undefined) {
      return { name, why: "is wider than any header carries: narrow it" };
    }
    if (opening.width > widestJacksBear(loaded, opening.wall)) {
      return {
        name,
        why: "is wider than its wall's jack studs bear: narrow it, or frame the walls in 2x6",
      };
    }
    if (opening.sill + opening.height + header > studTop(loaded, opening.wall)) {
      return {
        name,
        why: "would need a header deeper than fits under the top plate: make it shorter, or raise the walls",
      };
    }
    // A heavier roof can double an opening's jacks, and a pair needs a stud more wall at
    // the corner and beside its neighbors than one did: the engine's
    // `opening_too_close_to_corner` and `openings_too_close`, arrived at by the snow.
    if (jackPlies(loaded, opening) > jackPlies(spec, opening)) {
      const clearance = edgeClearance(loaded, opening);
      const crowded = spec.openings.some(
        (other) =>
          other.id !== opening.id &&
          other.wall === opening.wall &&
          Math.max(other.offset - opening.offset - opening.width, opening.offset - other.offset - other.width) <
            gapBetween(loaded, opening, other),
      );
      if (
        crowded ||
        opening.offset < clearance ||
        opening.offset + opening.width > wallRun(loaded, opening.wall) - clearance
      ) {
        return {
          name,
          why: "would need its jack studs doubled, and there is not the wall beside it for them: slide it along the wall, or narrow it",
        };
      }
    }
  }
  // The roof's own joints: a tie pulls harder the more snow there is, and past a dozen
  // nails at its heel the engine refuses the roof (`tie_heel_overloaded`).
  if (heelNails(loaded) > MAX_HEEL_NAILS) {
    return {
      name: "the rafter ties",
      why: "would pull on their heel joints harder than a dozen 16d nails hold: steepen the roof, tighten the rafter spacing, or open the roof on a ridge beam",
    };
  }
  return undefined;
}

/**
 * What the shopper calls an opening: "Door 1", "Window 2", counted along the list by
 * kind. Its id is for the JSON; nobody standing in a yard calls a door `door-1`.
 */
export function openingName(spec: Spec, index: number): string {
  const opening = spec.openings[index];
  if (opening === undefined) {
    return "An opening";
  }
  const nth = spec.openings
    .slice(0, index + 1)
    .filter((candidate) => candidate.type === opening.type).length;
  return `${opening.type === "door" ? "Door" : "Window"} ${String(nth)}`;
}

/**
 * The hand a door is when the shopper has not chosen one: what the engine states unstated
 * (`defaultHand`) — a single door or a prehung unit hinged on the left, a pair opening on
 * the right.
 */
export function handOf(opening: Opening): DoorHand {
  return opening.hand ?? (opening.doorStyle === "siteBuiltDouble" ? "right" : "left");
}

/** The swing a prehung door is ordered with when the shopper has not chosen one (`kDefaultSwing`). */
export function swingOf(opening: Opening): DoorSwing {
  return opening.swing ?? "in";
}
